/**
 * Home feed — Variant B (Magazine grid) renderer.
 *
 * Replaces the legacy `initHomeFeed()` body. Renders the home feed into
 * `#homeFeed` using a unified magazine-style grid:
 *
 *   ┌─── HERO ──────────────────────┐   full-width, brand gradient
 *   │   plate · name · 3 stats      │
 *   └───────────────────────────────┘
 *   ┌─ RECALL (if any) ─────────────┐   full-width, red — urgent
 *   ┌─ WEATHER WARN (if any) ───────┐   full-width, blue — caution
 *   ┌─ FUEL ───┐ ┌─ MOSACHIM ───┐     2-column mini cards
 *   ┌─ CHARGE ─┐ ┌─ WEATHER ────┐
 *   ┌─ SEARCH ─┐ ┌─ MESSAGES ───┐
 *   ┌─ NEWS ────────────────────┐     full-width
 *   ┌─ TRAFFIC ─────────────────┐     full-width
 *
 * All data sources are read in parallel; cards render empty-state if
 * their data isn't available. Click handlers wire to existing legacy
 * globals so behaviour stays identical to before.
 */

import { auth, ref } from '../../core/firebase.js';
import { LocalState } from '../../core/localState.js';
import { normalize as normalizePlate, badgeHtml as plateBadgeHtml } from '../../shared/plate.js';
import { escape as esc } from '../../shared/escape.js';
import { createLogger } from '../../core/logger.js';
import { ensureGovBundle, getLastOwnership } from '../vehicle/ownership.service.js';
import { getStatus as getLicenseStatus } from '../vehicle/license.service.js';
import * as fuelCard    from '../infoFeed/fuelPrices.card.js';
import * as weatherCard from '../infoFeed/weather.card.js';
import * as newsCard    from '../infoFeed/news.card.js';
import * as trafficCard from '../infoFeed/traffic.card.js';
import * as unreadBadge from '../messaging/unreadBadge.js';
import { build as buildDailyBrief, getCachedElement as getCachedBrief } from './dailyBrief.js';
import { shouldShowBoldCard as shouldShowPhotoDiagBold } from '../photoDiagnostic/usage.js';
import { renderCard as renderDisabledBadgeCard } from '../disabledBadge/index.js';
import { renderCard as renderUsedCarCheckCard } from '../usedCarCheck/index.js';

const log = createLogger('home');

/** Re-entry guard. Prevents an infinite render→event→render loop when
 *  something inside the render path (or downstream) fires an event that
 *  ends up calling `render()` again before the first call has finished. */
let isRendering = false;

/**
 * Render the entire home feed into `#homeFeed`. Idempotent — re-rendering
 * replaces all content atomically (no flicker).
 */
export async function render() {
  if (isRendering) {
    log.info('render() called while rendering — skipping');
    return;
  }
  const host = document.getElementById('homeFeed');
  if (!host) return;
  isRendering = true;
  try {
    await renderInternal(host);
  } catch (err) {
    log.warn('render failed:', err);
  } finally {
    isRendering = false;
  }
}

async function renderInternal(host) {
  host.hidden = false;

  // Pull data in parallel. Any failure leaves that card empty / hidden.
  const plate = normalizePlate(LocalState.plate ?? '');
  const uid = auth().currentUser?.uid;

  /** @type {[any, any, any, any, any, any]} */
  const [gov, ownership, license, recalls, lastSearch, lastChat] = await Promise.all([
    plate ? readGov(plate) : Promise.resolve({}),
    plate ? getLastOwnership(plate).catch(() => ({ dateISO: null })) : Promise.resolve({ dateISO: null }),
    plate ? getLicenseStatus(plate).catch(() => ({ status: 'unknown', daysLeft: null })) : Promise.resolve({ status: 'unknown', daysLeft: null }),
    plate ? loadOpenRecalls(plate) : Promise.resolve([]),
    Promise.resolve(readLastSearch()),
    Promise.resolve(readLastChat()),
  ]);

  // Trigger gov bundle fetch lazily (cached 24h) — used so future renders
  // benefit from fresh data; doesn't block first paint.
  if (plate) void ensureGovBundle(plate).catch(() => {});

  // Build DOM atomically.
  host.replaceChildren();
  host.classList.add('home-grid-b');

  // Daily brief — shown above the hero, once per local day.
  //
  // To avoid the half-second flash on re-renders, we attach a cached
  // element synchronously (if one exists from a previous render in this
  // session) and only fall back to the async build on the very first
  // render of the page. Subsequent renders reuse the same DOM node —
  // `appendChild` moves nodes between parents, so a single instance is
  // shared across renders.
  const briefSlot = document.createElement('div');
  briefSlot.className = 'b-row-full';
  host.appendChild(briefSlot);
  const cachedBrief = getCachedBrief();
  if (cachedBrief) {
    briefSlot.appendChild(cachedBrief);
  } else {
    void buildDailyBrief().then((el) => { if (el) briefSlot.appendChild(el); }).catch(() => {});
  }

  host.appendChild(renderHero({ plate, gov, ownership, license, recalls }));

  // Urgent / personalised — only if active
  if (recalls.length > 0) host.appendChild(renderRecallCard(recalls[0], recalls.length));

  // Weather warning slot — auto-hides if no warnings
  const weatherWarnEl = document.createElement('div');
  weatherWarnEl.className = 'b-row-full';
  host.appendChild(weatherWarnEl);
  void weatherCard.renderInto(weatherWarnEl);

  // 🔬 Photo Diagnostic — bold "feature spotlight" card. Auto-fades after
  // 14 days OR 3 successful diagnoses (see photoDiagnostic/usage.js).
  // The mini card in the grid below stays permanently.
  if (shouldShowPhotoDiagBold()) {
    host.appendChild(renderPhotoDiagnosticHero());
  }

  // ♿ Disabled-badge check — public lookup card. Always visible.
  // Sibling to the diag hero (same visual weight, blue gradient instead
  // of indigo). Has an inline plate input + button rather than being a
  // single click-target like the diag hero, so users can check a plate
  // without leaving the home screen.
  host.appendChild(renderDisabledBadgeCard());

  // 🔍 Used-car-check — public "is this used car a lemon?" lookup.
  // Aggregates 3 CKAN datasets (vehicles + ownership + recalls), computes
  // red flags (frequent transfers / was rental/taxi / expired license /
  // open recalls / late registration). Violet gradient — third unique
  // hero color, so all three sibling cards read as distinct features.
  host.appendChild(renderUsedCarCheckCard());

  // 2x grid: fuel · garages · charging · weather · search · messages
  const grid = document.createElement('div');
  grid.className = 'b-grid';

  grid.appendChild(renderFuelMini());
  grid.appendChild(renderServicesMini('🔧', 'מוסכים', 'ברדיוס 3 ק"מ', 'showGaragesScreen', 'garage', '12'));
  grid.appendChild(renderServicesMini('⚡', 'תחנות טעינה', 'פעילות בסביבה', 'showChargeStations', 'charge', '8', true));
  grid.appendChild(renderWeatherCurrent());
  grid.appendChild(renderLastSearchMini(lastSearch));
  grid.appendChild(renderMessagesMini(lastChat, uid));
  // 🔬 Photo Diagnostic — camera-based issue analysis (always visible
  // mini-card; the bold hero above appears only for first 14 days / <3 uses).
  grid.appendChild(renderServicesMini('🔬', 'אבחון תקלה', 'צלם — קבל אבחון', 'openPhotoDiagnostic', 'diag'));
  // 🎫 Fine Appeal — photo a fine → AI drafts an appeal letter.
  grid.appendChild(renderServicesMini('🎫', 'ערעור קנס', 'AI כותב מכתב משפטי', 'openFineAppeal', 'fine'));
  // 🧬 Vehicle DNA — statistical breakdown of the user's specific car.
  grid.appendChild(renderServicesMini('🧬', 'DNA הרכב', 'השוואה לדגמים זהים', 'openVehicleDNA', 'dna'));
  // AI chat entry was previously a grid card; it now lives as a global
  // floating button (`src/features/ai/aiFab.js`) + a contextual chip
  // inside the daily brief. Removed from the grid to avoid duplication.

  host.appendChild(grid);

  // Full-width info cards: news + traffic
  const newsEl = document.createElement('div');
  newsEl.className = 'b-row-full';
  host.appendChild(newsEl);
  void newsCard.renderInto(newsEl);

  const trafficEl = document.createElement('div');
  trafficEl.className = 'b-row-full';
  host.appendChild(trafficEl);
  trafficCard.renderInto(trafficEl);

  // Activate fuel card (separate render after grid placement)
  const fuelHost = grid.querySelector('[data-mini="fuel"]');
  if (fuelHost) void fuelCard.renderInto(fuelHost);

  // Repaint unread-count badges. The messages mini-card has a placeholder
  // `[data-unread-badge]` element that the badge service updates whenever
  // a new inbox event arrives — but right after first render we need it
  // to show the CURRENT count too, without waiting for the next event.
  try { unreadBadge.refresh(); } catch (_) { /* ignore */ }

  // NOTE: we deliberately do NOT call `checkAlertStatusAndUpdateDot` /
  // `alertStatus.refresh` from here — the alertStatus service already
  // subscribes to `auth:ready` and `vehicle:changed` (see
  // src/features/vehicle/alertStatus.js::init). Triggering it manually
  // from render() created a loop:
  //   render → alertStatus.refresh → license:status event →
  //   initHomeFeed listener in bootstrap → render → ...
}

// ====================================================================
// Data loaders
// ====================================================================

async function readGov(plate) {
  try {
    const snap = await ref(`vehicles/${plate}/gov/vehicle`).once('value');
    return snap.val() || {};
  } catch (_) { return {}; }
}

async function loadOpenRecalls(plate) {
  try {
    const snap = await ref(`vehicles/${plate}/recallsNotified`).once('value');
    const map = snap.val() || {};
    const out = [];
    for (const [id, rec] of Object.entries(map)) {
      const r = /** @type {any} */ (rec);
      if (r && !r.markedHandledAt) {
        out.push({ id, desc: String(r.desc || 'קריאת בטיחות'), website: r.website || null });
      }
    }
    return out;
  } catch (_) { return []; }
}

function readLastSearch() {
  try { return JSON.parse(localStorage.getItem('c2c_lastSearch') || 'null'); }
  catch (_) { return null; }
}
function readLastChat() {
  try { return JSON.parse(localStorage.getItem('c2c_lastChat') || 'null'); }
  catch (_) { return null; }
}

// ====================================================================
// Section: HERO
// ====================================================================

function renderHero({ plate, gov, ownership, license, recalls }) {
  const el = document.createElement('div');
  el.className = 'b-hero';
  el.setAttribute('role', 'button');
  el.setAttribute('tabindex', '0');

  if (!plate) {
    // No plate yet — invite the user to register a vehicle
    el.innerHTML = `
      <div class="b-hero-top">
        <div class="b-hero-empty">🚗 לא משויך רכב</div>
      </div>
      <div class="b-hero-name">הוסף רכב כדי להתחיל</div>
      <div class="b-hero-meta">קח פחות מדקה — נדרש טלפון, לוחית, שנת ייצור.</div>
    `;
    el.addEventListener('click', () => safeCall('openReplaceModal'));
    return el;
  }

  const plateBadge = plateBadgeHtml(plate);
  const manufacturer = String(gov.tozeret_nm || '').trim();
  const commercialName = String(gov.kinuy_mishari || gov.degem_nm || '').trim();
  const fullModel = [manufacturer, commercialName].filter(Boolean).join(' ') || 'הרכב שלי';
  const year = gov.shnat_yitzur || LocalState.carYear || '';
  const ownedFor = ownership.dateISO ? ownedDurationText(ownership.dateISO) : '';

  const licDays = Number.isFinite(license.daysLeft) ? Math.max(0, license.daysLeft) : '?';
  const licDot = license.status === 'red' ? '🔴' : license.status === 'orange' ? '🟡' : license.status === 'green' ? '🟢' : '⚪';
  const searchCount = (function () {
    try { return Object.keys(JSON.parse(localStorage.getItem('c2c_searchHistory') || '[]')).length; }
    catch (_) { return 0; }
  })();

  el.innerHTML = `
    <div class="b-hero-top">
      <div class="b-hero-plate-wrap">${plateBadge}</div>
      <button class="b-hero-refresh" type="button" aria-label="רענן" title="רענן נתונים">🔄</button>
    </div>
    <div class="b-hero-name">${esc(fullModel)}</div>
    <div class="b-hero-meta">${year ? `שנת ${esc(String(year))}` : ''}${ownedFor ? ` · ${esc(ownedFor)}` : ''}</div>
    <div class="b-hero-stats">
      <div class="b-hero-stat">
        <div class="b-hero-stat-val">${esc(String(licDays))}</div>
        <div class="b-hero-stat-lbl">${licDot} ימי רישיון</div>
      </div>
      <div class="b-hero-stat">
        <div class="b-hero-stat-val">${esc(String(recalls.length))}</div>
        <div class="b-hero-stat-lbl">${recalls.length > 0 ? '🔴' : '🟢'} קריאות פתוחות</div>
      </div>
      <div class="b-hero-stat">
        <div class="b-hero-stat-val">${esc(String(searchCount))}</div>
        <div class="b-hero-stat-lbl">🔍 חיפושים</div>
      </div>
    </div>
  `;

  // Click hero → open my-car panel; clicks on refresh button alone re-render.
  el.addEventListener('click', (e) => {
    if (/** @type {HTMLElement} */ (e.target).closest('.b-hero-refresh')) {
      void render();
      return;
    }
    safeCall('openMyCarOnly');
  });
  return el;
}

// ====================================================================
// Section: RECALL urgent card
// ====================================================================

/**
 * Bold "feature spotlight" card for Photo Diagnostic. Sits above the grid
 * and gets auto-hidden after the user has been exposed to it enough times
 * (14 days OR 3 successful diagnoses). The small grid card below stays
 * permanently.
 */
function renderPhotoDiagnosticHero() {
  const el = document.createElement('div');
  el.className = 'pdiag-hero b-row-full';
  el.setAttribute('role', 'button');
  el.setAttribute('tabindex', '0');
  el.innerHTML = `
    <div class="pdiag-hero-badge">✨ חדש</div>
    <div class="pdiag-hero-icon">🔬</div>
    <div class="pdiag-hero-body">
      <div class="pdiag-hero-title">אבחון תקלה ב־AI</div>
      <div class="pdiag-hero-sub">צלם חיווי בדשבורד, נזילה או נזק — אבחון מקצועי תוך 15 שניות</div>
    </div>
    <div class="pdiag-hero-arrow">←</div>
  `;
  el.addEventListener('click', () => safeCall('openPhotoDiagnostic'));
  el.addEventListener('keydown', (e) => {
    const ke = /** @type {KeyboardEvent} */ (e);
    if (ke.key === 'Enter' || ke.key === ' ') {
      ke.preventDefault();
      safeCall('openPhotoDiagnostic');
    }
  });
  return el;
}

function renderRecallCard(rc, total) {
  const el = document.createElement('div');
  el.className = 'b-mini-card cat-urgent b-row-full';
  el.innerHTML = `
    <div class="b-top-row">
      <div class="b-badge">⚠️</div>
      <span class="b-pill">${total > 1 ? `דחוף · ${total}` : 'דחוף'}</span>
    </div>
    <div>
      <div class="b-title">קריאת יצרן פתוחה</div>
      <div class="b-sub" style="margin-top:4px;">${esc(rc.desc.slice(0, 140))}</div>
    </div>
    ${rc.website ? `<a class="b-mini-cta" href="${esc(rc.website)}" target="_blank" rel="noopener" style="background:linear-gradient(135deg,#ef4444,#b91c1c);color:#fff;display:inline-block;padding:8px 14px;border-radius:8px;text-decoration:none;font-weight:700;font-size:12px;">פתח אתר היצרן ↗</a>` : ''}
  `;
  return el;
}

// ====================================================================
// Section: Grid mini cards
// ====================================================================

function renderFuelMini() {
  const el = document.createElement('div');
  el.className = 'b-mini-card cat-fuel b-card-clickable';
  el.setAttribute('data-mini', 'fuel');
  // Initial skeleton — fuelCard.renderInto replaces this after GPS resolves
  el.innerHTML = `
    <div class="b-top-row"><div class="b-badge">⛽</div><span class="b-chev">›</span></div>
    <div>
      <div class="b-title">דלק זול</div>
      <div class="b-sub">⏳ מאתר תחנות...</div>
    </div>
  `;
  return el;
}

/**
 * @param {string} icon
 * @param {string} title
 * @param {string} sub
 * @param {string} fnName
 * @param {string} [category]  e.g. 'garage' / 'charge' — picks the colored badge skin
 * @param {string} [value]     big number/text shown above sub
 * @param {boolean} [live]     show a live-dot in the corner instead of a chevron
 */
function renderServicesMini(icon, title, sub, fnName, category, value, live) {
  const el = document.createElement('div');
  el.className = `b-mini-card b-card-clickable${category ? ' cat-' + category : ''}`;
  const cornerEl = live
    ? '<span class="b-live-dot"></span>'
    : '<span class="b-chev">›</span>';
  const valueLine = value ? `<div class="b-value">${esc(value)}</div>` : '';
  el.innerHTML = `
    <div class="b-top-row"><div class="b-badge">${esc(icon)}</div>${cornerEl}</div>
    <div>
      <div class="b-title">${esc(title)}</div>
      ${valueLine}
      <div class="b-sub">${esc(sub)}</div>
    </div>
  `;
  el.addEventListener('click', () => safeCall(fnName));
  return el;
}

function renderWeatherCurrent() {
  const el = document.createElement('div');
  el.className = 'b-mini-card cat-weather b-card-clickable';
  el.innerHTML = `
    <div class="b-top-row"><div class="b-badge">☁️</div><span class="b-chev">›</span></div>
    <div>
      <div class="b-title">מזג אוויר</div>
      <div class="b-sub">תחזית, חמסין, אזהרות</div>
    </div>
  `;
  // Open the full weather modal (existing legacy `openWeatherModal`).
  // We deliberately don't pre-fetch a current-temperature snapshot here —
  // the previous version hit open-meteo on every home render, which
  // produced noisy 502/CORS errors in the console when the public API
  // was overloaded. The full modal has its own resilient weather flow.
  el.addEventListener('click', () => safeCall('openWeatherModal'));
  return el;
}

function renderLastSearchMini(lastSearch) {
  const el = document.createElement('div');
  el.className = 'b-mini-card cat-search b-card-clickable';
  if (!lastSearch || !lastSearch.plate) {
    el.innerHTML = `
      <div class="b-top-row"><div class="b-badge">🔍</div></div>
      <div>
        <div class="b-title">חיפוש</div>
        <div class="b-sub">חפש לוחית רכב כדי להתחיל</div>
      </div>
    `;
    el.addEventListener('click', () => {
      const input = /** @type {HTMLInputElement | null} */ (document.getElementById('searchPlate'));
      if (input) input.focus();
    });
    return el;
  }
  const fmt = (p) => {
    const d = String(p || '').replace(/\D/g, '');
    if (d.length === 8) return `${d.slice(0,3)}-${d.slice(3,5)}-${d.slice(5)}`;
    if (d.length === 7) return `${d.slice(0,2)}-${d.slice(2,5)}-${d.slice(5)}`;
    return d;
  };
  el.innerHTML = `
    <div class="b-top-row"><div class="b-badge">🔍</div><span class="b-chev">›</span></div>
    <div>
      <div class="b-title">חיפוש אחרון</div>
      <div class="b-value" style="font-size:18px;font-family:monospace;letter-spacing:1px;">${esc(fmt(lastSearch.plate))}</div>
      <div class="b-sub">${lastSearch.model ? esc(lastSearch.model) : 'לחץ לחיפוש מחדש'}</div>
    </div>
  `;
  el.addEventListener('click', () => {
    const input = /** @type {HTMLInputElement | null} */ (document.getElementById('searchPlate'));
    if (input) {
      input.value = fmt(lastSearch.plate);
      const w = /** @type {any} */ (window);
      if (typeof w.searchVehicle === 'function') w.searchVehicle();
    }
  });
  return el;
}

function renderMessagesMini(lastChat, uid) {
  const el = document.createElement('div');
  el.className = 'b-mini-card cat-msg b-card-clickable';
  // The unread-count pill (`[data-unread-badge]`) is painted by
  // src/features/messaging/unreadBadge.js — it queries the document for
  // any element with that attribute and updates it on every inbox event.
  // We render it hidden by default; the badge service shows it when count > 0.
  const unreadBadgeHtml = `<span class="b-pill" data-unread-badge style="display:none;">0</span>`;
  if (!lastChat || !lastChat.body) {
    el.innerHTML = `
      <div class="b-top-row"><div class="b-badge">💬</div>${unreadBadgeHtml}</div>
      <div>
        <div class="b-title">הודעות</div>
        <div class="b-sub">אין הודעות חדשות</div>
      </div>
    `;
  } else {
    el.innerHTML = `
      <div class="b-top-row"><div class="b-badge">💬</div>${unreadBadgeHtml}</div>
      <div>
        <div class="b-title">הודעה אחרונה</div>
        <div class="b-value" style="font-size:14px;">"${esc(String(lastChat.body).slice(0, 40))}${String(lastChat.body).length > 40 ? '...' : ''}"</div>
        <div class="b-sub">לחץ לפתוח את כל ההודעות</div>
      </div>
    `;
  }
  // The visible "🔔 ההודעות שלי" tab in the drawer uses data-tab="notifications"
  // (not "messages"). Calling 'messages' would open a different / older renderer
  // that doesn't match what users see in the personal-area tab.
  el.addEventListener('click', () => safeCall('openAccountDrawer', 'notifications'));
  return el;
}

// ====================================================================
// Helpers
// ====================================================================

/** Safe call to a global function — silent no-op if missing. */
function safeCall(name, ...args) {
  try {
    const w = /** @type {any} */ (window);
    const fn = w[name];
    if (typeof fn === 'function') fn(...args);
  } catch (e) { log.warn(name + ' threw:', e); }
}

async function getGps() {
  try {
    const raw = localStorage.getItem('c2c_fuel_lastGps');
    if (raw) {
      const c = JSON.parse(raw);
      if (c && c.at && Date.now() - c.at < 10 * 60 * 1000 && c.lat && c.lng) {
        return { lat: c.lat, lng: c.lng };
      }
    }
  } catch (_) { /* ignore */ }
  if (typeof navigator === 'undefined' || !navigator.geolocation) return null;
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null),
      { maximumAge: 10 * 60 * 1000, timeout: 5000 },
    );
  });
}

function ownedDurationText(dateISO) {
  const ms = Date.now() - Date.parse(dateISO);
  if (!Number.isFinite(ms) || ms < 0) return '';
  const totalMonths = Math.floor(ms / (30 * 86400000));
  if (totalMonths === 0) return 'ברשותך פחות מחודש';
  if (totalMonths < 12) return `ברשותך ${totalMonths} חודשים`;
  const y = Math.floor(totalMonths / 12);
  const m = totalMonths % 12;
  if (m === 0) return `ברשותך ${y} שנים`;
  return `ברשותך ${y} שנים ו-${m} חודשים`;
}

function weatherCodeToText(code) {
  const c = Number(code) || 0;
  if (c === 0) return 'בהיר';
  if (c >= 1 && c <= 3) return 'מעונן חלקית';
  if (c === 45 || c === 48) return 'ערפילי';
  if (c >= 51 && c <= 67) return 'גשם';
  if (c >= 71 && c <= 77) return 'שלג';
  if (c >= 80 && c <= 82) return 'ממטרים';
  if (c >= 95) return 'סופה';
  return 'מעונן';
}
